<section class="page-offset section-pad">
  <div class="container">
    <div class="text-center mb-5">
      <h1 class="fw-bold">{{ i18n.t('plans.title') }}</h1>
      <p class="text-secondary">{{ i18n.t('plans.subtitle') }}</p>
    </div>
    @if (isLoadingPlanCatalog) {
      <div class="soft-card bg-white p-5" aria-live="polite">
        <app-loading-spinner [label]="i18n.t('plans.loadingCatalog')"></app-loading-spinner>
      </div>
    } @else {
    <div class="row g-4">
      @for (plan of plans; track plan.key) {
        <div class="col-md-4">
          <div class="soft-card bg-white h-100 p-4" [class.border-primary]="plan.highlighted">
            <h2 class="h4">{{ i18n.t('plans.' + plan.key + '.name') }}</h2>
            <div class="d-flex align-items-center justify-content-between gap-2">
              <p class="display-6 fw-bold mb-0">{{ displayPrice(plan.key) }}</p>
              @if (isCurrentPlan(plan.key)) {
                <span class="badge rounded-pill text-bg-success">{{ i18n.t('plans.currentPlan') }}</span>
              }
            </div>
            <p class="text-secondary">{{ i18n.t('plans.' + plan.key + '.description') }}</p>
            @if (plan.entitlements) {
              <div class="small text-secondary mb-3">
                <span class="me-3"><strong>{{ displayMonthlyLimit(plan.key, 'monthlyCoverLetterGenerations') }}</strong> {{ i18n.t('plans.dynamicLimits.coverLetters') }}</span>
                <span><strong>{{ displayStorageLimit(plan.key) }}</strong> {{ i18n.t('plans.dynamicLimits.files') }}</span>
              </div>
            }
            <ul class="list-unstyled">
              @for (feature of planFeatures(plan.key); track feature) {
                <li><i class="bi bi-check2 text-primary me-2"></i>{{ feature }}</li>
              }
            </ul>
            <button class="btn btn-outline-primary w-100" type="button" (click)="openPlanDetails(plan)">{{ i18n.t('plans.previewDetails') }}</button>
          </div>
        </div>
      }
    </div>
    }
  </div>
</section>

@if (selectedPlan) {
  <div class="modal-backdrop-custom" [class.is-closing]="isPlanDetailClosing" (click)="closePlanDetails()"></div>
  <div class="plan-detail-modal" [class.is-closing]="isPlanDetailClosing" role="dialog" aria-modal="true" aria-labelledby="planDetailTitle">
    <div class="plan-detail-modal__card soft-card bg-white">
      <div class="plan-detail-modal__header d-flex justify-content-between align-items-start gap-3">
        <div>
          <h2 id="planDetailTitle" class="h4 mb-1">{{ i18n.t('plans.' + selectedPlan.key + '.name') }}</h2>
          <p class="text-secondary mb-0">{{ i18n.t('plans.' + selectedPlan.key + '.description') }}</p>
          @if (currentEntitlements?.plan === selectedPlan.code) {
            <span class="badge rounded-pill text-bg-success mt-2">{{ i18n.t('plans.currentPlan') }}</span>
          }
        </div>
        <button class="btn btn-sm btn-outline-secondary" type="button" (click)="closePlanDetails()" [attr.aria-label]="i18n.t('plans.closeDetails')"><i class="bi bi-x-lg"></i></button>
      </div>

      <div class="plan-detail-modal__body">
        <div class="plan-price-panel mb-3">
          <strong>{{ isPaidPlan(selectedPlan.key) ? billingPrice(selectedPlan.key) : i18n.t('plans.' + selectedPlan.key + '.price') }}</strong>
          <span>{{ isPaidPlan(selectedPlan.key) ? i18n.t('plans.paymentPending') : i18n.t('plans.freePlanNoBilling') }}</span>
        </div>

        @if (isPaidPlan(selectedPlan.key)) {
          <div class="mb-3">
            <h3 class="h6">{{ i18n.t('plans.billingPeriod') }}</h3>
            <div class="billing-options" role="list">
              @for (option of billingOptions; track option.key) {
                <button
                  class="billing-option"
                  type="button"
                  role="listitem"
                  [class.active]="selectedBillingPeriod === option.key"
                  [attr.aria-pressed]="selectedBillingPeriod === option.key"
                  (click)="selectBillingPeriod(option.key)">
                  <span class="billing-option__period">{{ billingPeriodLabel(option.key) }}</span>
                  <strong>{{ billingPrice(selectedPlan.key, option.key) }}</strong>
                  <small>{{ billingSavings(selectedPlan.key, option.key) }}</small>
                </button>
              }
            </div>
          </div>
        }

        <div class="row g-3 mb-3">
          <div class="col-md-6">
            <h3 class="h6">{{ i18n.t('plans.included') }}</h3>
            <ul class="mb-0">
              @for (feature of planFeatures(selectedPlan.key); track feature) { <li>{{ feature }}</li> }
            </ul>
          </div>
          <div class="col-md-6">
            <h3 class="h6">{{ i18n.t('plans.limits') }}</h3>
            <ul class="mb-0">
              @for (limit of planLimits(selectedPlan.key); track limit) { <li>{{ limit }}</li> }
              @if (selectedPlan.entitlements) {
                <li>{{ displayMonthlyLimit(selectedPlan.key, 'monthlyDetailedFitReports') }} {{ i18n.t('plans.dynamicLimits.fitReports') }}</li>
                <li>{{ displayMonthlyLimit(selectedPlan.key, 'monthlyCoverLetterGenerations') }} {{ i18n.t('plans.dynamicLimits.coverLetters') }}</li>
                <li>{{ displayMonthlyLimit(selectedPlan.key, 'monthlyProfileParagraphGenerations') }} {{ i18n.t('plans.dynamicLimits.profileParagraphs') }}</li>
                <li>{{ displayMonthlyLimit(selectedPlan.key, 'monthlyOfferComparisons') }} {{ i18n.t('plans.dynamicLimits.offerComparisons') }}</li>
              }
            </ul>
          </div>
        </div>

        @if (isPaidPlan(selectedPlan.key)) {
          <div class="alert alert-info mb-3">{{ i18n.t('plans.providerReady') }}</div>
          <div class="alert alert-light border mb-3 small">{{ i18n.t('plans.promoCodeHint') }}</div>
          <div class="alert alert-light border mb-3 small">
            {{ i18n.t('plans.legalConsentPrefix') }}
            <a routerLink="/cgu">{{ i18n.t('legal.cguShort') }}</a>
            {{ i18n.t('plans.legalConsentMiddle') }}
            <a routerLink="/cgv">{{ i18n.t('legal.cgvShort') }}</a>{{ i18n.t('plans.legalConsentSuffix') }}
            <div class="form-check mt-3">
              <input class="form-check-input" type="checkbox" id="acceptTerms" name="acceptTerms" [(ngModel)]="hasAcceptedTerms" [disabled]="isStartingCheckout || isOpeningPortal">
              <label class="form-check-label" for="acceptTerms">{{ i18n.t('plans.acceptTermsLabel') }}</label>
            </div>
            <div class="form-check mt-2">
              <input class="form-check-input" type="checkbox" id="acceptImmediatePerformance" name="acceptImmediatePerformance" [(ngModel)]="hasAcceptedImmediatePerformance" [disabled]="isStartingCheckout || isOpeningPortal">
              <label class="form-check-label" for="acceptImmediatePerformance">
                {{ i18n.t('plans.immediatePerformanceLabel') }}
                <a routerLink="/withdrawal">{{ i18n.t('plans.withdrawalInformation') }}</a>
              </label>
            </div>
          </div>
          @if (paymentActionMessageKey) {
            <div class="alert alert-warning mb-3">{{ i18n.t(paymentActionMessageKey) }}</div>
          }
          @if (isViewingCurrentPaidPlanPeriod) {
            <div class="alert alert-success mb-3">{{ i18n.t('plans.alreadyCurrentPlanPeriod') }}</div>
          }
          <button class="btn btn-primary w-100" type="button" (click)="startCheckout()" [disabled]="isCheckoutDisabled" [attr.aria-busy]="isStartingCheckout">
            @if (isStartingCheckout) { <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span> }
            {{ isStartingCheckout ? i18n.t('plans.startingCheckout') : i18n.t('plans.startCheckout') }} · {{ billingPrice(selectedPlan.key, selectedBillingPeriod) }}
          </button>
          @if (canManageBilling) {
            <button class="btn btn-outline-primary w-100 mt-2" type="button" (click)="openCustomerPortal()" [disabled]="isStartingCheckout || isOpeningPortal" [attr.aria-busy]="isOpeningPortal">
              @if (isOpeningPortal) { <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span> }
              {{ isOpeningPortal ? i18n.t('plans.openingPortal') : i18n.t('plans.manageBilling') }}
            </button>
          }
        }
      </div>
    </div>
  </div>
}

<section class="section-pad pt-0">
  <div class="container">
    <div class="pricing-rationale soft-card bg-white p-4 p-md-5">
      <h2 class="h4">{{ i18n.t('plans.pricingRationale.title') }}</h2>
      <p class="text-secondary mb-3">{{ i18n.t('plans.pricingRationale.body') }}</p>
      <ul class="mb-0">
        @for (item of pricingRationaleItems(); track item) {
          <li>{{ item }}</li>
        }
      </ul>
    </div>
  </div>
</section>
